import React, { useEffect, useState, useRef } from 'react';
import { Trophy, Gamepad2, Monitor, ArrowRight, Sparkles } from 'lucide-react';
import { mockData } from '../mock';
import ServiceDetailModal from './ServiceDetailModal';
import { useLanguage } from '../contexts/LanguageContext';

const iconMap = {
  Trophy,
  Gamepad2,
  Monitor,
  Sparkles
};

const EventimeServices = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleServiceClick = (service, actionType) => {
    if (actionType === 'learn-more') {
      setSelectedService(service);
      setIsModalOpen(true);
    } else {
      // Direct WhatsApp inquiry
      const serviceKeys = ['sports', 'esports', 'expo'];
      const serviceIndex = service.id - 1;
      const serviceKey = serviceKeys[serviceIndex];
      const serviceName = t(`services.items.${serviceKey}.title`);
      
      const whatsappMessage = t('serviceInquiry').replace('{serviceName}', serviceName);
      const encodedMessage = encodeURIComponent(whatsappMessage);
      const whatsappURL = `https://wa.me/+6285179649711?text=${encodedMessage}`;
      
      window.open(whatsappURL, '_blank');
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedService(null);
  };

  const handleWhatsAppInquiry = (serviceTitle) => {
      const whatsappMessage = t('whatsapp.serviceInquiry').replace('{serviceName}', serviceTitle);
      const encodedMessage = encodeURIComponent(whatsappMessage);
      const whatsappURL = `https://wa.me/+6285179649711?text=${encodedMessage}`;
      window.open(whatsappURL, '_blank');
    };

  return (
    <>
      <section id="services" className="section" ref={sectionRef}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-giant)' }}>
            <h2 
              className={`heading-1 animated ${isVisible ? 'fadeIn' : ''}`}
              style={{ marginBottom: 'var(--spacing-medium)' }}
            >
              {t('services.title')}
            </h2>
            
            <p 
              className={`body-large animated ${isVisible ? 'fadeIn' : ''}`}
              style={{ 
                maxWidth: '700px', 
                margin: '0 auto',
                transitionDelay: '0.2s'
              }}
            >
              {t('services.subtitle')}
            </p>
          </div>

          {/* Services Grid */}
          <div 
            className="network-grid"
            style={{ 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 'var(--spacing-xl)',
              padding: '0',
              maxWidth: '1400px',
              margin: '0 auto'
            }}
          >
            {mockData.services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              const serviceKeys = ['sports', 'lifestyle', 'esports', 'expo'];
              const serviceKey = serviceKeys[index];
              
              return (
                <div 
                  key={service.id}
                  className={`network-card hover-lift animated ${isVisible ? 'fadeIn' : ''}`}
                  style={{ 
                    transitionDelay: `${0.3 + index * 0.1}s`,
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {/* Service Image */}
                  <div style={{
                    width: '100%',
                    height: '200px',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    marginBottom: 'var(--spacing-medium)',
                    position: 'relative'
                  }}>
                    <img 
                      src={service.image}
                      alt={t(`services.items.${serviceKey}.title`)}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'scale(1.05)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'scale(1)';
                      }}
                    />
                    
                    {/* Icon Overlay */}
                    <div style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      background: 'linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-primary-hover) 100%)',
                      borderRadius: '16px',
                      padding: '12px',
                      color: 'var(--text-white)',
                      boxShadow: 'var(--shadow-medium)'
                    }}>
                      <IconComponent size={24} className="icon-enhanced" />
                    </div>
                  </div>

                  {/* Service Content */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <h3 className="network-card-title" style={{ marginBottom: 'var(--spacing-medium)' }}>
                      {t(`services.items.${serviceKey}.title`)}
                    </h3>
                    
                    <p className="network-card-content" style={{ marginBottom: 'var(--spacing-large)', flex: 1 }}>
                      {t(`services.items.${serviceKey}.description`)}
                    </p>

                    {/* Bold CTA Button */}
                    <button
                      className="btn-primary hover-lift"
                      onClick={() => {
                        const serviceName = t(`services.items.${serviceKey}.title`);
                        
                        // Menggunakan kamus bahasa (t) supaya otomatis ganti English/Indo
                        const message = t('serviceInquiry').replace('{serviceName}', serviceName);
                        const encodedMessage = encodeURIComponent(message);
                        
                        const whatsappURL = `https://wa.me/+6285179649711?text=${encodedMessage}`;
                        window.open(whatsappURL, '_blank');
                      }}
                      style={{
                        width: '100%',
                        fontSize: '1rem',
                        fontWeight: '700',
                        padding: '14px 20px',
                        background: 'linear-gradient(135deg, var(--brand-primary) 0%, #d4ff00 100%)',
                        color: 'var(--brand-dark)',
                        border: 'none',
                        boxShadow: '0 4px 16px rgba(231, 76, 60, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 24px rgba(231, 76, 60, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(231, 76, 60, 0.3)';
                      }}
                    >
                      <span>{t(`services.items.${serviceKey}.cta`)}</span>
                      <ArrowRight size={18} className="icon-enhanced" style={{ marginLeft: '8px' }} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Detail Modal */}
      <ServiceDetailModal 
        service={selectedService}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </>
  );
};

export default EventimeServices;
